"use client";

// Route-level error boundary for the public site. Two jobs: record the failed
// render in the website error log (/admin/errors), and show the visitor a
// branded page with a way forward instead of an unstyled crash screen.
//
// A server-render failure reaches the browser with its message stripped and
// only a `digest` — the full message is logged server-side by the
// onRequestError hook in instrumentation.ts and lines up by that digest.
import { useEffect } from "react";
import Link from "next/link";
import { Container } from "@/components/Container";
import { reportError } from "@/lib/reportError";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportError({
      kind: "render",
      message: error.message || "Page failed to render",
      source: error.digest ? `digest:${error.digest}` : null,
      stack: error.stack ?? null,
    });
  }, [error]);

  return (
    <section className="border-t border-brand-line bg-brand-paper py-24 md:py-32">
      <Container>
        <div className="mx-auto max-w-2xl text-center">
          <p className="mono-meta uppercase text-brand-accent-dark">Something went wrong</p>
          <h1 className="t-display mt-5 text-brand-ink">This page didn&rsquo;t load</h1>
          <p className="mt-5 text-base leading-relaxed text-brand-slate md:text-lg">
            Sorry — something on our side failed while loading this page. The
            issue has been logged for our team. Try again, or head back to the
            homepage.
          </p>
          <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
            <button type="button" onClick={reset} className="btn btn-primary">
              Try again
            </button>
            <Link href="/" className="btn btn-secondary">
              Go to homepage
            </Link>
          </div>
          <p className="mt-8 text-sm text-brand-muted">
            Need help now? Call{" "}
            <a href="tel:18003157871" className="text-brand-accent-dark">1800 315 7871</a> or email{" "}
            <a href="mailto:info@tablespace.com" className="text-brand-accent-dark">info@tablespace.com</a>.
          </p>
        </div>
      </Container>
    </section>
  );
}
